// X144 (Grant 2026-10-26: ONE admin "Competitions" page grouping the five format list pages, "this should allow
// filtering and appropriate redirect put in place"): the pure words and routes behind /admin/clubs/{clubId}/competitions.
// The page's filters and paging live in the route query (router.replace); the old per-format LIST addresses redirect
// here with that format picked; the create wizards are hosted here (?create=<format>). Nothing here talks to the API.
import {formatExplainers, FORMAT_KEYS} from '~/utils/formatExplainers';
import {formatMinor} from '~/utils/adminActionCopy';

export const COMPETITIONS_ROUTE = 'ClubCompetitions';

/** The old list paths (the INDEX pages only; a competition's own pages under them keep their addresses). */
export const OLD_LIST_PATHS = Object.freeze({
  leagues: 'league', americanos: 'americano', ladders: 'ladder', tournaments: 'tournament', meets: 'meet',
});

/** The per-competition admin page under each old list path, for an old ?newCycle=<id> link. */
const OLD_LIST_MANAGE = Object.freeze({leagues: 'leagues', americanos: 'americanos'});

/**
 * The status pill's choices; `current` (running & starting soon) is the page's default and leaves the address clean.
 * X148 (R71 L2): a just-created competition (no cycle, no dates, not published) is the API's `settingUp`, part of the
 * default view, so the admin sees what they just made; `finished` ("Not running") is for ones that ran.
 */
export const STATUS_OPTIONS = Object.freeze([
  {value: 'current', label: 'Running & upcoming', statuses: ['running', 'upcoming', 'settingUp']},
  {value: 'finished', label: 'Not running', statuses: ['finished']},
  {value: 'archived', label: 'Archived', statuses: ['archived']},
  {value: 'all', label: 'Everything', statuses: ['running', 'upcoming', 'settingUp', 'finished', 'archived']},
]);
export const DEFAULT_STATUS = 'current';
export const PAGE_SIZE = 25;

const one = (v) => (Array.isArray(v) ? v[0] : v);
const isFormat = (v) => FORMAT_KEYS.includes(v);

/** The page's own route, optionally with a format picked or a create wizard to open. */
export function competitionsRoute(clubId, {format = null, create = null, status = null} = {}) {
  const query = {};
  if (format && isFormat(format)) query.format = format;
  if (create && isFormat(create)) query.create = create;
  if (status && status !== DEFAULT_STATUS && STATUS_OPTIONS.some(o => o.value === status)) query.status = status;
  return Object.keys(query).length ? {name: COMPETITIONS_ROUTE, params: {clubId}, query} : {name: COMPETITIONS_ROUTE, params: {clubId}};
}

/**
 * An old list address → where it goes now, or null when the path is not one. The format is picked; every other
 * query key rides along; the old create markers (?wizard=1, ?action=create) become ?create=<format> so the wizard
 * still opens; an old ?newCycle=<id> on a box-league or americano list opens that competition's own page (its
 * new-cycle wizard opens there by itself when it has no cycle yet).
 */
export function oldListRedirect(path, query = {}) {
  const m = String(path ?? '').match(/^\/admin\/clubs\/([^/]+)\/(leagues|ladders|tournaments|americanos|meets)\/?$/);
  if (!m) return null;
  const [, clubId, old] = m;
  const format = OLD_LIST_PATHS[old];
  const rest = {...query};
  const wantsWizard = String(one(rest.wizard) ?? '') === '1' || String(one(rest.action) ?? '') === 'create';
  const newCycle = one(rest.newCycle);
  delete rest.wizard;
  delete rest.action;
  delete rest.actionId;
  delete rest.newCycle;
  if (newCycle && OLD_LIST_MANAGE[old]) {
    return {path: `/admin/clubs/${clubId}/${OLD_LIST_MANAGE[old]}/${newCycle}`, query: rest};
  }
  const next = {...rest, format};
  if (wantsWizard) next.create = format;
  return {path: `/admin/clubs/${clubId}/competitions`, query: next};
}

/** The page's state from the route query (bad values fall back quietly). */
export function listStateFromQuery(query = {}) {
  const format = String(one(query.format) ?? '');
  const status = String(one(query.status) ?? '');
  const sport = String(one(query.sport) ?? '');
  const page = Number.parseInt(String(one(query.page) ?? '1'), 10);
  return {
    format: isFormat(format) ? format : '',
    status: STATUS_OPTIONS.some(o => o.value === status) ? status : DEFAULT_STATUS,
    q: String(one(query.q) ?? ''),
    sport: /^[0-9a-f-]{36}$/i.test(sport) ? sport.toLowerCase() : '',
    page: Number.isFinite(page) && page > 0 ? page : 1,
  };
}

/** The route query for a state, keeping every other key (dialogs, the create marker); defaults and empties leave the address clean. */
export function queryFromState(state, existing = {}) {
  const next = {...existing};
  const set = (key, value) => { if (value === undefined || value === null || value === '') delete next[key]; else next[key] = value; };
  set('format', state.format || undefined);
  set('status', state.status && state.status !== DEFAULT_STATUS ? state.status : undefined);
  set('q', state.q?.trim() || undefined);
  set('sport', state.sport || undefined);
  set('page', state.page && state.page > 1 ? String(state.page) : undefined);
  return next;
}

/** Whether two states ask for the same rows (paging aside). */
export function sameFilters(a, b) {
  return (a.format ?? '') === (b.format ?? '') && (a.status ?? DEFAULT_STATUS) === (b.status ?? DEFAULT_STATUS)
      && (a.q ?? '').trim() === (b.q ?? '').trim() && (a.sport ?? '') === (b.sport ?? '');
}

/** The API's filters[...] for a state (useCrudApi flattens them). */
export function listFilters(state) {
  const filters = {statuses: (STATUS_OPTIONS.find(o => o.value === state.status) ?? STATUS_OPTIONS[0]).statuses};
  if (state.format) filters.formats = [state.format];
  if (state.sport) filters.sportIds = [state.sport];
  const q = state.q?.trim();
  if (q) filters.search = q;
  return filters;
}

/**
 * The format pills: "All · Box leagues 3 · Ladders 1". Only the formats the club has, in the formats' order, with the
 * count each would show under the other filters; none at all when the club has one format (nothing to choose).
 */
export function formatPills(formatCounts = {}, clubFormats = []) {
  const have = FORMAT_KEYS.filter(k => clubFormats.includes(k));
  if (have.length <= 1) return [];
  const total = have.reduce((n, k) => n + (formatCounts[k] ?? 0), 0);
  return [
    {key: '', label: 'All', count: total},
    ...have.map(k => ({key: k, label: formatExplainers[k].name, count: formatCounts[k] ?? 0})),
  ];
}

/** The status chip beside a row's name. */
export function statusChip(row) {
  switch (row?.status) {
    case 'running': return {label: 'Running', cls: 'tl-chip--ok'};
    case 'upcoming': return {label: 'Starts soon', cls: 'tl-chip--brand'};
    case 'settingUp': return {label: 'Setting up', cls: 'tl-chip--warn'};
    case 'archived': return {label: 'Archived', cls: ''};
    default: return {label: 'Not running', cls: ''};
  }
}

/** "3 waiting" when people wait on an admin for this competition. */
export function waitingWords(row) {
  const n = row?.joinRequests ?? 0;
  return n > 0 ? `${n} waiting` : '';
}

/** The entrants column: "8 teams" / "12 players" (the API's entrants already means players for ladders, americanos and meets). */
export function entrantsWords(row) {
  const n = row?.entrants ?? 0;
  const [one, many] = entrantWord(row);
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * X165 (R76 L5): what a competition's entrants are called. Box leagues and tournaments enter teams, but a singles
 * one (the API's playersPerTeam = 1) enters players, and "2 teams" on a singles cup read wrong. Ladders, americanos
 * and meets are always players. A row from an older API (no playersPerTeam) keeps saying teams.
 */
export function entrantWord(row) {
  // X166 (R77 L5): a doubles ladder enters teams too (its tab says "teams in ladder"); an older API row without the
  // field keeps saying players for a ladder
  const teams = row?.type === 'ladder'
    ? (row?.playersPerTeam ?? 1) > 1
    : (row?.type === 'league' || row?.type === 'tournament') && row?.playersPerTeam !== 1;
  return teams ? ['team', 'teams'] : ['player', 'players'];
}

/**
 * X167 (Grant 2026-10-27): the row's fee, "£5.00" (two decimals, the club's currency), from the API's additive feeMinor
 * (a ladder's or tournament's entry fee, a meet's price per date, a box or americano league's current cycle's fee);
 * '' when free or from an older API row without the field.
 */
export function feeWords(row) {
  const minor = row?.feeMinor;
  if (minor == null || Number(minor) <= 0) return '';
  return formatMinor(Number(minor), row?.feeCurrency || 'gbp');
}

/**
 * Grant (2026-11-03, "Add a fee line to each row"): the row's one quiet fee line, the old per-format list pages' words
 * (uat's Entry fee / Fee facts): "£5.00 entry" on a ladder or tournament, "£5.00 entry per cycle" on a box or americano
 * league (each cycle carries its own fee; the row's feeMinor is the current cycle's), "£5.00 per session" on a meet (its
 * price per date). The standard price only: a membership type's own price is not on the row. '' when free (free shows
 * nothing) or from an older API row without the field; the page also hides it while the club's payments are hidden.
 */
export function feeLine(row) {
  const money = feeWords(row);
  if (!money) return '';
  switch (row?.type) {
    case 'league':
    case 'americano': return `${money} entry per cycle`;
    case 'meet': return `${money} per session`;
    default: return `${money} entry`;
  }
}

/** The mailer's filters for "Email players" on a row; meets have no competition filter there (null = no button). */
export function emailFilters(row, clubId) {
  const key = {league: 'leagueIds', ladder: 'ladderIds', tournament: 'tournamentIds', americano: 'americanoIds'}[row?.type];
  return key ? {clubIds: [clubId], [key]: [row.id]} : null;
}

/**
 * The words when the filters leave nothing, plain per status (X148, R71 L4: "No ladders not running" read badly):
 * "No ladders running or starting soon" · "No ladders that aren't running" · "No archived ladders" · "No ladders".
 */
export function emptyWords(state) {
  const what = state.format ? formatExplainers[state.format].name.toLowerCase() : 'competitions';
  const status = (STATUS_OPTIONS.find(o => o.value === state.status) ?? STATUS_OPTIONS[0]).value;
  const q = state.q?.trim();
  if (q) return `No ${what} match "${q}"`;
  switch (status) {
    case 'current': return `No ${what} running or starting soon`;
    case 'finished': return `No ${what} that aren't running`;
    case 'archived': return `No archived ${what}`;
    default: return `No ${what}`;
  }
}
